import { Typography, styled } from "@mui/material";

const backgroundColor = "--qa-background-color";
const borderWidth = "--qa-border-width";

interface PairProps {
  reversed?: boolean;
}
export const Pair = styled("div", {
  shouldForwardProp: (propName: keyof PairProps) => propName !== "reversed",
})<PairProps>(({ theme, reversed }) => ({
  display: "flex",
  flexDirection: reversed ? "column-reverse" : "column",
  gap: theme.spacing(2),
}));

export const Question = styled("div")(({ theme }) => ({
  [backgroundColor]: theme.palette.grey[100],
  display: "flex",
  flexDirection: "row-reverse",
}));

export const Answer = styled("div")(({ theme }) => ({
  display: "flex",
  overflow: "hidden",

  [backgroundColor]: theme.palette.common.white,
  "&.error": {
    [backgroundColor]: theme.palette.error.background,
  },
}));

export const Border = styled("div")(({ theme }) => ({
  [borderWidth]: theme.spacing(1),
  borderTop: `var(${borderWidth}) solid var(${backgroundColor})`,
  overflow: "hidden",
  [`${Question} &`]: {
    borderRight: `var(${borderWidth}) solid transparent`,
  },
  [`${Answer} &`]: {
    borderLeft: `var(${borderWidth}) solid transparent`,
  },
}));

export const Body = styled("div")(({ theme }) => ({
  backgroundColor: `var(${backgroundColor})`,
  border: 0,
  flex: 1,
  padding: theme.spacing(1, 2, 2, 2),
  [`${Question} &`]: {
    display: "inline-flex",
  },
  [`${Answer} &`]: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

export const UserActionButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
}));

export const AutomaticallyDiscardedMessage = styled(Typography)(
  ({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  })
);

export const Text = styled(Typography)({
  [`${Answer}.error &`]: {
    color: "#560202",
    fontWeight: 300,
  },
}) as typeof Typography;

export const ModeChangeStatement = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  position: "relative",
  "::before": {
    backgroundColor: theme.palette.text.disabled,
    content: '""',
    display: "block",
    height: "1px",
    left: 0,
    position: "absolute",
    top: "50%",
    width: "100%",
  },
}));

export const ModeChangeStatementText = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  color: theme.palette.text.disabled,
  padding: theme.spacing(0, 1),
  // Draw statement text above pseudo element
  zIndex: 5,
})) as typeof Typography;

export const ModelExpertWelcomePrompt = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

interface ModelExpertWelcomePromptLineProps {
  isSamplePrompt?: boolean;
}

export const ModelExpertWelcomePromptLine = styled("div", {
  shouldForwardProp: (propName: keyof ModelExpertWelcomePromptLineProps) =>
    propName !== "isSamplePrompt",
})<ModelExpertWelcomePromptLineProps>(({ theme, isSamplePrompt }) => [
  isSamplePrompt
    ? {
        color: theme.palette.text.secondary,
        fontStyle: "italic",
      }
    : {},
]);

export const References = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const ReferencesList = styled("dl")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBlockStart: 0,
  marginBlockEnd: 0,
}));

export const ReferenceItem = styled("dt")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
